import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, AlertCircle, CheckCircle2, Loader2, Maximize2, ZoomIn } from 'lucide-react';
import './QRScanner.css';

/**
 * Componente Scanner QR moderno con supporto camera device
 * - Auto-focus e rilevamento automatico
 * - Feedback visivo e sonoro/haptico
 * - Fallback graceful se camera non disponibile
 * - Testato su Android/iOS
 */
const QRScanner = ({ onScan, onClose, title = "Scansiona QR Code", subtitle = "Inquadra il codice QR" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const isProcessingRef = useRef(false); // Ref per check sincrono

  // Inizializza scanner e rileva camere disponibili
  useEffect(() => {
    initializeScanner();
    
    return () => {
      stopScanning();
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, []);

  const initializeScanner = async () => {
    try {
      // Richiedi permessi camera
      const devices = await Html5Qrcode.getCameras();
      
      if (devices && devices.length > 0) {
        setCameras(devices);
        
        // Preferisci camera posteriore su mobile
        const rearCamera = devices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('posteriore')
        );
        
        const cameraToUse = rearCamera || devices[0];
        setSelectedCamera(cameraToUse.id);
        await startScanning(cameraToUse.id);
      } else {
        setError('Nessuna camera disponibile su questo dispositivo');
        setPermissionDenied(true);
      }
    } catch (err) {
      console.error('Errore inizializzazione scanner:', err);
      
      if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
        setPermissionDenied(true);
        setError('Permesso camera negato. Abilita l\'accesso alla camera nelle impostazioni del browser.');
      } else if (err.name === 'NotFoundError') {
        setError('Nessuna camera trovata su questo dispositivo');
        setPermissionDenied(true);
      } else if (err.name === 'NotSupportedError') {
        setError('Il browser non supporta l\'accesso alla camera. Prova con Chrome o Safari.');
        setPermissionDenied(true);
      } else {
        setError('Impossibile accedere alla camera. Verifica le impostazioni del dispositivo.');
        setPermissionDenied(true);
      }
    }
  };

  const startScanning = async (cameraId) => {
    try {
      html5QrCodeRef.current = new Html5Qrcode('qr-scanner-region');
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        formatsToSupport: ['QR_CODE'],
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true
        }
      };

      await html5QrCodeRef.current.start(
        cameraId,
        config,
        onScanSuccess,
        onScanFailure
      );

      setIsScanning(true);
      setError(null);
    } catch (err) {
      console.error('Errore avvio scanner:', err);
      setError('Impossibile avviare lo scanner');
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current && isScanning) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Errore stop scanner:', err);
      }
      setIsScanning(false);
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    // BLOCCO IMMEDIATO con ref (sincrono) - check PRIMA di qualsiasi altra cosa
    if (isProcessingRef.current) {
      console.debug('Scansione bloccata (processing in corso)');
      return;
    }
    
    // Throttle: previeni scansioni multiple dello stesso QR in rapida successione
    const now = Date.now();
    const timeSinceLastScan = now - lastScanTimeRef.current;
    
    // Se è passato meno di 3 secondi dall'ultima scansione, ignora
    if (timeSinceLastScan < 2000) {
      console.debug('Scansione ignorata (throttle 2s)');
      return;
    }
    
    // Evita scansioni duplicate rapide
    if (lastResult === decodedText) {
      console.debug('Scansione ignorata (token duplicato)');
      return;
    }
    
    // IMPOSTA SUBITO il flag (sia ref che state)
    isProcessingRef.current = true;
    setIsProcessing(true);
    lastScanTimeRef.current = now;
    setLastResult(decodedText);
    
    console.log('✅ Scansione QR accettata:', decodedText);

    // Feedback haptico (se supportato)
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]); // Pattern di vibrazione: corto-pausa-corto
    }

    // Feedback sonoro
    playSuccessSound();
    
    // Mostra successo visivo
    setScanSuccess(true);

    // NON fermare lo scanner - lascialo attivo per scansioni continue
    // await stopScanning(); // RIMOSSO per modalità continua

    // Chiama callback con risultato
    if (onScan) {
      onScan(decodedText, decodedResult);
    }

    // Reset dopo 2 secondi per permettere nuova scansione
    scanTimeoutRef.current = setTimeout(() => {
      setScanSuccess(false);
      setLastResult(null);
      setIsProcessing(false);
      isProcessingRef.current = false;
      lastScanTimeRef.current = 0;
      // Scanner rimane attivo per scansione successiva
    }, 2000); // Ridotto da 3 a 2 secondi per velocità
  };

  const onScanFailure = (error) => {
    // Ignora errori comuni di scansione (nessun QR trovato nel frame)
    if (error.includes('NotFoundException') || error.includes('No MultiFormat Readers')) {
      return;
    }
    console.debug('Scan error (ignorato):', error);
  };

  const playSuccessSound = () => {
    try {
      // Crea un beep sintetico con Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequenza del beep
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3; // Volume

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1); // Durata 100ms
    } catch (err) {
      // Silently fail se audio non supportato
      console.debug('Audio feedback non disponibile');
    }
  };

  const handleClose = async () => {
    await stopScanning();
    if (onClose) {
      onClose();
    }
  };

  const handleCameraChange = async (e) => {
    const newCameraId = e.target.value;
    await stopScanning();
    setSelectedCamera(newCameraId);
    await startScanning(newCameraId);
  };

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">
        {/* Header */}
        <div className="qr-scanner-header">
          <div>
            <h2 className="qr-scanner-title">
              <Camera size={24} />
              {title}
            </h2>
            <p className="qr-scanner-subtitle">{subtitle}</p>
          </div>
          <button onClick={handleClose} className="qr-scanner-close-btn" aria-label="Chiudi">
            <X size={24} />
          </button>
        </div>

        {/* Scanner Area */}
        <div className="qr-scanner-content">
          {!permissionDenied ? (
            <>
              <div className="qr-scanner-video-container">
                <div id="qr-scanner-region" ref={scannerRef}></div>
                
                {/* Overlay con angoli per inquadratura */}
                <div className="qr-scanner-overlay-box">
                  <div className="corner corner-tl"></div>
                  <div className="corner corner-tr"></div>
                  <div className="corner corner-bl"></div>
                  <div className="corner corner-br"></div>
                  
                  {scanSuccess && (
                    <div className="scan-success-indicator">
                      <CheckCircle2 size={64} />
                      <p>Scansione riuscita!</p>
                    </div>
                  )}
                </div>

                {/* Loading Indicator */}
                {!isScanning && !error && !scanSuccess && (
                  <div className="qr-scanner-loading">
                    <Loader2 size={48} className="spinner" />
                    <p>Inizializzazione camera...</p>
                  </div>
                )}
              </div>

              {/* Istruzioni */}
              {isScanning && !scanSuccess && (
                <div className="qr-scanner-instructions">
                  <Maximize2 size={20} />
                  <p>Posiziona il QR code all'interno del riquadro</p>
                </div>
              )}

              {/* Selezione Camera (se multiple disponibili) */}
              {cameras.length > 1 && (
                <div className="qr-scanner-camera-select">
                  <label htmlFor="camera-select">
                    <Camera size={18} />
                    Seleziona Camera:
                  </label>
                  <select 
                    id="camera-select"
                    value={selectedCamera || ''} 
                    onChange={handleCameraChange}
                    disabled={!isScanning}
                  >
                    {cameras.map((camera) => (
                      <option key={camera.id} value={camera.id}>
                        {camera.label || `Camera ${camera.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          ) : (
            /* Fallback quando camera non disponibile */
            <div className="qr-scanner-fallback">
              <div className="fallback-icon">
                <AlertCircle size={64} />
              </div>
              <h3>Scanner Camera Non Disponibile</h3>
              <p className="fallback-message">{error}</p>
              
              <div className="fallback-instructions">
                <h4>Come risolvere:</h4>
                <ul>
                  <li>
                    <strong>Permessi:</strong> Assicurati di aver consentito l'accesso alla camera quando richiesto
                  </li>
                  <li>
                    <strong>Browser:</strong> Usa Chrome, Safari o Firefox aggiornati
                  </li>
                  <li>
                    <strong>HTTPS:</strong> La camera funziona solo su connessioni sicure (https://)
                  </li>
                  <li>
                    <strong>Impostazioni:</strong> Controlla le impostazioni di privacy del browser
                  </li>
                </ul>
              </div>

              <div className="fallback-actions">
                <button onClick={initializeScanner} className="retry-btn">
                  Riprova
                </button>
                <button onClick={handleClose} className="cancel-btn">
                  Annulla
                </button>
              </div>
            </div>
          )}

          {/* Errore generico */}
          {error && !permissionDenied && (
            <div className="qr-scanner-error">
              <AlertCircle size={24} />
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Footer con informazioni */}
        <div className="qr-scanner-footer">
          <div className="footer-info">
            <AlertCircle size={16} />
            <span>Mantieni il QR code stabile e ben illuminato</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
